import {
  formMediaFullUrl,
  formMediaVersionedUrl,
  getMediaCacheInvalidatedEventName,
  invalidateMediaCache,
} from "../../utilities/imageUtilities";
import { useEffect, useMemo, useState, useRef } from "react";

function useMediaCacheVersion(url, enabled = true) {
  const [version, setVersion] = useState(0);
  const fullUrl = useMemo(() => formMediaFullUrl(url), [url]);

  useEffect(() => {
    if (typeof window === "undefined" || !enabled) {
      return undefined;
    }

    const eventName = getMediaCacheInvalidatedEventName();

    function onMediaInvalidated(event) {
      if (event?.detail?.url === fullUrl) {
        setVersion((current) => current + 1);
      }
    }

    window.addEventListener(eventName, onMediaInvalidated);
    return () => window.removeEventListener(eventName, onMediaInvalidated);
  }, [fullUrl, enabled]);

  return version;
}

export default function MediaImage({
  url,
  refreshTrigger = false,
  alt = 'Media',
  dataTest = 'media-image',
  className = '',
  ...props
}) {
  // url is expected to be the http_path from the sara rest api; formMediaVersionedUrl
  // resolves it to a full URL and appends cache version info when needed.
  const [objectUrl, setObjectUrl] = useState(null);
  const currentUrlRef = useRef(null);
  const version = useMediaCacheVersion(url, !refreshTrigger);
  const src = useMemo(
    () => formMediaVersionedUrl(url, refreshTrigger, version > 0 ? version : undefined),
    [url, refreshTrigger, version]
  );

  useEffect(() => {
    if (!refreshTrigger) {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
      setObjectUrl(null);
      return undefined;
    }

    const controller = new AbortController();

    const loadImage = async () => {
      try {
        const res = await fetch(src, {
          cache: refreshTrigger ? "reload" : "default",
          signal: controller.signal,
        });

        if (res.ok) {
          console.log(`Image loaded successfully: refreshTrigger: ${refreshTrigger}, src: ${src}`);
          invalidateMediaCache(url);
        } else {
          throw new Error(`Image Fetch Error: src: ${src}, HTTP ${res.status}`);
        }

        const blob = await res.blob();
        const newUrl = URL.createObjectURL(blob);

        // cleanup previous URL
        if (currentUrlRef.current) {
          URL.revokeObjectURL(currentUrlRef.current);
        }

        currentUrlRef.current = newUrl;
        setObjectUrl(newUrl);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(`Image load failed: src: ${src}`, err);
        }
      }
    };

    loadImage();

    return () => controller.abort();
  }, [src, refreshTrigger, url]);

  useEffect(() => {
    return () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
      }
    };
  }, []);

  if (refreshTrigger && !objectUrl) return null;

  const imageSrc = refreshTrigger ? objectUrl : src;

  return <img src={imageSrc} alt={alt} data-test={dataTest} className={className} {...props} />;
}

// Backward-compatible export: callers can keep importing MediaImageWithCacheRefresh.
export function MediaImageWithCacheRefresh(props) {
  return <MediaImage {...props} />;
}
